import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AddEmailLogin } from '../components/EmailAuth';
import { toast } from 'sonner';
import { UserCircle, Mail, Phone, MapPin, Wallet, Save, Loader2 } from 'lucide-react';

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: '', alt_phone: '', email: '', upi_id: '',
    address: { line1: '', village: '', pincode: '', landmark: '' },
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || '',
      alt_phone: user.alt_phone || '',
      email: user.email || '',
      upi_id: user.upi_id || '',
      address: {
        line1: user.address?.line1 || '',
        village: user.address?.village || '',
        pincode: user.address?.pincode || '',
        landmark: user.address?.landmark || '',
      },
    });
  }, [user?.id, user?.alt_phone, user?.email]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/auth/me', form);
      await refresh();
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (!user) {
    return <div className="max-w-2xl mx-auto px-4 py-12 text-center text-ink/60">Please log in to view your profile.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-12">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#FFF3E0] text-[#D35400] grid place-items-center"><UserCircle className="w-6 h-6"/></div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">My profile</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl">{user.name || 'Welcome'}</h1>
        </div>
        {user.serial_id && (
          <span data-testid="my-serial-id" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#1B5E20] text-white px-3 py-1.5 text-xs font-bold shadow-sm font-mono" title="Your BharatCart member ID">
            {user.serial_id}
          </span>
        )}
      </div>
      <p className="text-ink/70 mt-2 max-w-xl">Add your address, alt phone and UPI ID for faster checkout and refunds.</p>

      <div className="card-soft p-5 mt-6">
        <div className="font-heading font-bold text-base mb-3 flex items-center gap-2"><Phone className="w-4 h-4 text-[#D35400]"/>Contact</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Full name" testId="pf-name"><input className="pinp" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
          <Field label="Primary phone (login)" hint="Cannot be changed"><input className="pinp" value={user.phone} disabled/></Field>
          <Field label="Alternate phone" testId="pf-altphone"><input className="pinp" value={form.alt_phone} onChange={e=>setForm({...form, alt_phone:e.target.value.replace(/\D/g,'').slice(0,15)})} inputMode="numeric"/></Field>
          <Field label="Email" testId="pf-email"><input className="pinp" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} placeholder="you@example.com"/></Field>
          <Field label="UPI ID (for refunds)" testId="pf-upi"><input className="pinp" value={form.upi_id} onChange={e=>setForm({...form, upi_id:e.target.value})} placeholder="name@bank"/></Field>
        </div>
      </div>

      <div className="card-soft p-5 mt-4">
        <div className="font-heading font-bold text-base mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D35400]"/>Default delivery address</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Field label="Street / House" testId="pf-addr-line1"><input className="pinp" value={form.address.line1} onChange={e=>setForm({...form, address:{...form.address, line1:e.target.value}})}/></Field></div>
          <Field label="Village / Town" testId="pf-village"><input className="pinp" value={form.address.village} onChange={e=>setForm({...form, address:{...form.address, village:e.target.value}})}/></Field>
          <Field label="Pincode" testId="pf-pin"><input className="pinp" value={form.address.pincode} onChange={e=>setForm({...form, address:{...form.address, pincode:e.target.value.replace(/\D/g,'').slice(0,6)}})}/></Field>
          <div className="md:col-span-2"><Field label="Landmark (optional)" testId="pf-landmark"><input className="pinp" value={form.address.landmark} onChange={e=>setForm({...form, address:{...form.address, landmark:e.target.value}})}/></Field></div>
        </div>
      </div>

      <button data-testid="pf-save-btn" disabled={busy} onClick={save} className="btn-primary mt-5">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Saving…</> : <><Save className="w-4 h-4"/>Save profile</>}
      </button>

      <AddEmailLogin user={user}/>

      <style>{`.pinp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.pinp:focus{border-color:#E67E22;box-shadow:0 0 0 3px rgba(230,126,34,0.15)}.pinp:disabled{background:#f5f5f0;color:#999}`}</style>
    </div>
  );
}

function Field({ label, children, testId, hint }) {
  return (
    <label data-testid={testId} className="block">
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-ink/50 mt-1">{hint}</div>}
    </label>
  );
}
